import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getToken } from '../../utils/getToken';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://devapi.quickcare.ng/admin';

const OverviewCharts = ({period, country}) => {

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartData, setChartData] = useState({
        series: [{
            name: 'Patients',
            data: []
        }],
        options: {
            chart: {
                height: 350,
                type: 'bar',
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                        selection: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                        pan: false,
                        reset: false
                    }
                },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800,
                    animateGradually: {
                        enabled: true,
                        delay: 150
                    }
                },
                background: 'transparent',
                fontFamily: 'Inter, system-ui, sans-serif'
            },
            plotOptions: {
                bar: {
                    borderRadius: 8,
                    borderRadiusApplication: 'end',
                    columnWidth: '60%',
                    dataLabels: {
                        position: 'top'
                    },
                    colors: {
                        ranges: [{
                            from: 0,
                            to: 1000,
                            color: '#069125'
                        }],
                        backgroundBarColors: ['#f0f0f0'],
                        backgroundBarOpacity: 1,
                        backgroundBarRadius: 8
                    }
                }
            },
            dataLabels: {
                enabled: true,
                offsetY: -25,
                style: {
                    fontSize: '11px',
                    colors: ["#1a1a1a"],
                    fontWeight: 600,
                    fontFamily: 'Inter, system-ui, sans-serif'
                },
                formatter: function(val) {
                    return val.toLocaleString();
                }
            },
            grid: {
                show: true,
                borderColor: '#f0f0f0',
                strokeDashArray: 5,
                position: 'back',
                xaxis: {
                    lines: {
                        show: false
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                }
            },
            xaxis: {
                categories: [],
                position: 'bottom',
                axisBorder: {
                    show: true,
                    color: '#e0e0e0'
                },
                axisTicks: {
                    show: true,
                    color: '#e0e0e0'
                },
                labels: {
                    style: {
                        fontSize: '12px',
                        fontWeight: 500,
                        colors: '#666666',
                        fontFamily: 'Inter, system-ui, sans-serif'
                    },
                    rotate: -45,
                    rotateAlways: false,
                    hideOverlappingLabels: true
                },
                crosshairs: {
                    show: true,
                    fill: {
                        type: 'gradient',
                        gradient: {
                            colorFrom: '#069125',
                            colorTo: '#069125',
                            stops: [0, 100],
                            opacityFrom: 0.1,
                            opacityTo: 0.05,
                        }
                    },
                    stroke: {
                        color: '#069125',
                        width: 1
                    }
                }
            },
            yaxis: {
                title: {
                    text: 'Number of Patients',
                    style: {
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#666666',
                        fontFamily: 'Inter, system-ui, sans-serif'
                    }
                },
                labels: {
                    style: {
                        fontSize: '11px',
                        fontWeight: 500,
                        colors: '#888888',
                        fontFamily: 'Inter, system-ui, sans-serif'
                    },
                    formatter: function(val) {
                        return val.toLocaleString();
                    }
                },
                axisBorder: {
                    show: false
                },
                axisTicks: {
                    show: false
                }
            },
            tooltip: {
                enabled: true,
                theme: 'light',
                style: {
                    fontSize: '12px',
                    fontFamily: 'Inter, system-ui, sans-serif'
                },
                y: {
                    formatter: function(val) {
                        return val.toLocaleString() + ' patients';
                    }
                },
                marker: {
                    show: true,
                    fillColors: ['#069125']
                },
                fill: {
                    type: 'solid',
                    gradient: {
                        enabled: false
                    }
                }
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'light',
                    type: 'vertical',
                    shadeIntensity: 0.3,
                    gradientToColors: ['#057a1f'],
                    inverseColors: false,
                    opacityFrom: 1,
                    opacityTo: 0.8,
                    stops: [0, 100]
                }
            },
            legend: {
                show: true,
                position: 'top',
                horizontalAlign: 'right',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: '12px',
                labels: {
                    colors: '#666666'
                },
                markers: {
                    radius: 6,
                    fillColors: ['#069125']
                }
            },
            title: {
                text: 'Monthly Patient Registrations',
                align: 'left',
                style: {
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#1a1a1a',
                    fontFamily: 'Inter, system-ui, sans-serif'
                }
            },
            noData: {
                text: 'No data available',
                align: 'center',
                verticalAlign: 'middle',
                style: {
                    fontSize: '14px',
                    color: '#999999',
                    fontFamily: 'Inter, system-ui, sans-serif'
                }
            }
        }
    });

    // Function to abbreviate month names
    const abbreviateMonth = (monthName) => {
        const monthMap = {
            'January': 'Jan',
            'February': 'Feb',
            'March': 'Mar',
            'April': 'Apr',
            'May': 'May',
            'June': 'Jun',
            'July': 'Jul',
            'August': 'Aug',
            'September': 'Sep',
            'October': 'Oct',
            'November': 'Nov',
            'December': 'Dec'
        };
        return monthMap[monthName] || monthName;
    };

    useEffect(() => {
        setLoading(true);
        setError(null);

        const fetchData = async () => {
            const token = getToken();
            if (!token) {
                setError('No token found. Please login again.');
                setLoading(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch(`${baseURL}/overview/total-patients?start_date=${period.start_date}&end_date=${period.end_date}&country=${country}`, requestOptions);
                const result = await response.json();

                if (response.ok) {
                    const seriesData = Object.values(result?.data || {}).map(val => Number(val));
                    // Abbreviate month names in categories
                    const xaxisCategories = Object.keys(result?.data || {}).map(month => abbreviateMonth(month));
                    
                    setChartData(prev => ({
                        ...prev,
                        series: [{...prev.series[0], data: seriesData}],
                        options: {
                            ...prev.options,
                            xaxis: {
                                ...prev.options.xaxis,
                                categories: xaxisCategories
                            },
                            title: {
                                ...prev.options.title,
                                text: `Monthly Patient Registrations - ${country.charAt(0).toUpperCase() + country.slice(1)}`
                            }
                        }
                    }));
                } else {
                    setError(result.message || 'Failed to fetch data');
                }
            } catch (error) {
                console.error("Fetch error:", error);
                setError(error.message || 'Network error');
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [period, country]);

    if (loading) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:p-8">
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#069125]"></div>
                    <p className="text-sm text-gray-500 mt-4">Loading chart data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:p-8">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <span className="text-4xl mb-3">⚠️</span>
                    <h3 className="text-lg font-semibold text-gray-700">Error Loading Chart</h3>
                    <p className="text-sm text-gray-500 mt-1">{error}</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-6 py-2 bg-[#069125] text-white text-sm font-medium rounded-xl hover:bg-[#057a1f] transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-4 lg:p-6 transition-all duration-300 hover:shadow-md">
            {/* Chart Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full"></div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">
                            Monthly Patient Registrations
                        </h2>
                        <p className="text-xs text-gray-400">
                            {country.charAt(0).toUpperCase() + country.slice(1)} • Period: {period.start_date} to {period.end_date}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                        Live
                    </span>
                </div>
            </div>

            {/* Chart */}
            <div className="w-full" style={{ minHeight: '300px' }}>
                <ReactApexChart 
                    options={chartData.options} 
                    series={chartData.series} 
                    type="bar" 
                    height={350}
                />
            </div>

            {/* Chart Footer */}
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>📊 Showing patient registrations by month</span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">Total: </span>
                    <span className="text-sm font-bold text-gray-800">
                        {chartData.series[0]?.data?.reduce((a, b) => a + b, 0).toLocaleString() || 0}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default OverviewCharts;